import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

const WIFE_FACTOR_MIN = 0.05;
const WIFE_FACTOR_MAX = 0.3;

function randomWifeFactor() {
  return WIFE_FACTOR_MIN + Math.random() * (WIFE_FACTOR_MAX - WIFE_FACTOR_MIN);
}

const TOAST_DURATION_MS = 2500;
const FAMILY_VIEW_MIN_AMOUNT = 10000;

const DiscountTab = ({ user, props }) => {
  const history = useHistory();
  const [discountLevelsInfo, setDiscountLevelsInfo] = useState(undefined);
  const [familyView, setFamilyView] = useState({ enabled: false, wifeFactor: null });
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const effectiveLevel = user.manualDiscountLevel != null ? user.manualDiscountLevel : user.discountLevel;
  const isLevelSetByAdmin = user.manualDiscountLevel != null && (user.discountLevel ?? 0) < user.manualDiscountLevel;

  const displayAmount = useCallback((amount) => {
    const value = amount ?? 0;
    if (!familyView.enabled || familyView.wifeFactor == null) return value;
    return Math.round(value * familyView.wifeFactor);
  }, [familyView.enabled, familyView.wifeFactor]);

  const toggleFamilyView = useCallback(() => {
    setFamilyView((prev) => {
      const nextEnabled = !prev.enabled;
      if (nextEnabled) {
        return { enabled: true, wifeFactor: randomWifeFactor() };
      }
      return { enabled: false, wifeFactor: null };
    });
    if (isCoarsePointer) {
      setToastMessage(familyView.enabled ? 'Режим сімейного перегляду вимкнено' : 'Активовано режим сімейного перегляду 😂');
      setTimeout(() => setToastMessage(null), TOAST_DURATION_MS);
    }
  }, [isCoarsePointer, familyView.enabled]);

  useEffect(() => {
    const mq = window.matchMedia('(pointer: coarse)');
    setIsCoarsePointer(mq.matches);
    const handler = (e) => setIsCoarsePointer(e.matches);
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  useEffect(() => {
    loadDiscountLevels();
  }, []);

  const loadDiscountLevels = () => {
    const token = localStorage.getItem('token');
    axios
      .get('/api/v1/user/discount-level', {
        headers: token ? { Authorization: token } : {},
      })
      .then(res => {
        const discountLevelsInfo = res.data.data;
        setDiscountLevelsInfo(discountLevelsInfo);
      })
      .catch(() => {
        if (props && props.history) {
          props.history.push("/login");
        } else {
          history.push("/login");
        }
      });
  };

  const defineClassName = (discountLevelInfo) => {
    if (discountLevelInfo.level <= effectiveLevel) {
      return "bi bi-check-circle-fill green-check-circle";
    } else {
      return "bi bi-lock-fill";
    }
  };

  const renderProgressBar = (discountLevelInfo) => {
    if (discountLevelInfo.hidden) return null;
    if (user.additionalDiscountInfo === undefined || user.additionalDiscountInfo === null) {
      return;
    }
    if (discountLevelInfo.level === user.additionalDiscountInfo.maxDiscountLevel.level && effectiveLevel === discountLevelInfo.level) {
      return (
        <p>Не зупиняйтесь, наступні рівні в процесі розробки :)</p>
      )
    } else if (discountLevelInfo.level < effectiveLevel) {
      return (
        <div className='progress' style={{ minHeight: '32px' }}>
          <div
            className='progress-bar bg-dark d-flex align-items-center justify-content-center'
            role='progressbar'
            style={{ width: '100%', color: '#fff', fontWeight: 500 }}>
            100%
          </div>
        </div>);
    } else if (discountLevelInfo.level === effectiveLevel) {
      const totalOrdersAmount = user.additionalDiscountInfo.totalOrdersAmount ?? 0;
      const currentRequired = user.additionalDiscountInfo.currentDiscountLevel?.requiredTotalAmount;
      const amountLeftToNext = user.additionalDiscountInfo.amountLeftToNextLevel ?? 0;
      const isManualLevel = isLevelSetByAdmin && discountLevelInfo.level === effectiveLevel;

      // Мануальний рівень може не мати required_total_amount — прогрес не показуємо
      if (currentRequired == null) {
        return <p className="text-muted mb-0">Персональний рівень знижки встановлено для вас.</p>;
      }

      let earned;
      let total;
      let percent;
      let headingText;
      let labelText;

      if (isManualLevel && totalOrdersAmount < currentRequired) {
        const nextRequired = totalOrdersAmount + amountLeftToNext;
        earned = totalOrdersAmount;
        total = nextRequired;
        percent = total > 0 ? Math.round((earned / total) * 100) : 0;
        headingText = `До наступного рівня: ${amountLeftToNext.toLocaleString('uk-UA')}грн`;
        labelText = 'до наступного рівня';
      } else {
        earned = user.additionalDiscountInfo.earnedAmountOnThisLevel ?? 0;
        total = earned + amountLeftToNext;
        percent = user.additionalDiscountInfo.earnedPercentOnThisLevel ?? 0;
        headingText = `До наступного рівня: ${amountLeftToNext.toLocaleString('uk-UA')}грн`;
        labelText = 'на цьому рівні';
      }

      return (
        <div>
          <h6><b>{headingText}</b></h6>
          <div className='progress mb-2' style={{ minHeight: '32px' }}>
            <div
              className='progress-bar bg-dark d-flex align-items-center justify-content-center'
              role='progressbar'
              style={{
                width: Math.min(Math.max(percent, 1), 100) + '%',
                minWidth: percent > 0 ? '44px' : undefined,
                color: '#fff',
                fontSize: '0.95rem',
                fontWeight: 500,
                textShadow: '0 1px 2px rgba(0,0,0,0.5)',
              }}>
              {percent > 0 ? `${percent}%` : ''}
            </div>
          </div>
          <div className='text-dark' style={{ fontSize: '0.9rem' }}>
            {earned.toLocaleString('uk-UA')} / {total.toLocaleString('uk-UA')} грн {labelText}
          </div>
        </div>);
    }
  };

  return (
    <div className="row personal-cabinet-tab-content">
      <div className="col">
        <h5 className='pb-1 text-center'><b>Персональна система знижок:</b></h5>
        {user.additionalDiscountInfo !== undefined && user.additionalDiscountInfo !== null && user.additionalDiscountInfo.currentDiscountLevel ? (
          <>
          <p>
            <span>Ваш рівень - <b>{user.additionalDiscountInfo.currentDiscountLevel.levelName}</b>. Ваша загальна сума </span>
            <span className="family-view-sum-wrap">
              успішних замовлень: <b>{displayAmount(user.additionalDiscountInfo.totalOrdersAmount).toLocaleString('uk-UA')}грн</b>
              {(user.additionalDiscountInfo.totalOrdersAmount ?? 0) >= FAMILY_VIEW_MIN_AMOUNT && (isCoarsePointer ? (
                <button
                  type="button"
                  className="btn btn-link p-0 ml-1 border-0 family-view-toggle-btn"
                  style={{ fontSize: '1.2rem', verticalAlign: 'middle', outline: 'none', boxShadow: 'none' }}
                  onClick={toggleFamilyView}
                  aria-label="Режим сімейного перегляду"
                >
                  {familyView.enabled ? '👀' : '🫣'}
                </button>
              ) : (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="family-view-tooltip">Режим сімейного перегляду 😂</Tooltip>}
                >
                  <button
                    type="button"
                    className="btn btn-link p-0 ml-1 border-0 family-view-toggle-btn"
                    style={{ fontSize: '1.2rem', verticalAlign: 'middle', outline: 'none', boxShadow: 'none' }}
                    onClick={toggleFamilyView}
                    aria-label="Режим сімейного перегляду"
                  >
                    {familyView.enabled ? '👀' : '🫣'}
                  </button>
                </OverlayTrigger>
              ))}
            </span>
          </p>
          {toastMessage && (
            <div
              className="family-view-toast"
              role="alert"
              aria-live="polite"
            >
              {toastMessage}
            </div>
          )}
          </>
        ) : null}

        {effectiveLevel !== undefined ?
          <Tabs
            defaultActiveKey={effectiveLevel}
            id='uncontrolled-tab-example'
            className='mb-3 discount-levels'
          >
            {discountLevelsInfo !== undefined
              ? discountLevelsInfo.map((discountLevelInfo, i) => {
                const isCurrentManualLevel = isLevelSetByAdmin && discountLevelInfo.level === effectiveLevel;
                const isHiddenLevel = Boolean(discountLevelInfo.hidden);
                return (
                  <Tab eventKey={discountLevelInfo.level}
                    title={<span><i
                      className={defineClassName(discountLevelInfo)}></i> {discountLevelInfo.levelName} </span>}>
                    {isCurrentManualLevel && !isHiddenLevel && (
                      <p className="mt-2 mb-0 font-italic font-weight-bold">Для вас встановлено адміністратором</p>
                    )}
                    <div className={isCurrentManualLevel && !isHiddenLevel ? 'discount-level-manual-description' : undefined}
                      dangerouslySetInnerHTML={{
                        __html: discountLevelInfo.userDescription,
                      }}
                    />
                    {renderProgressBar(discountLevelInfo)}
                  </Tab>
                );
              }) : null}
          </Tabs> : null}
      </div>
    </div>
  );
};

export default DiscountTab;
